import {
  createSlice,
  call,
  put,
  takeLatest,
  getHttpClient,
} from '@ufc/core'
import type { PayloadAction } from '@ufc/core'

// ============================================================
// Types
// ============================================================

export type KioskPacking = 'bulk' | 'bags'

export interface KioskQueueConfig {
  // ponytail: per-packing close state — closed[packaging] holds plant documentIds
  closed: { bulk: string[]; bags: string[] }
  autoOpenTimeBulk: string | null
  autoOpenTimeBags: string | null
}

export interface KioskConfigState {
  closed: { bulk: string[]; bags: string[] }
  autoOpenTimeBulk: string | null
  autoOpenTimeBags: string | null
  isLoaded: boolean
  error: string | null
}

// ============================================================
// Initial State
// ============================================================

const initialState: KioskConfigState = {
  closed: { bulk: [], bags: [] },
  autoOpenTimeBulk: null,
  autoOpenTimeBags: null,
  isLoaded: false,
  error: null,
}

// ============================================================
// Slice
// ============================================================

const kioskConfigSlice = createSlice({
  name: 'kioskConfig',
  initialState,
  reducers: {
    fetchKioskConfig: (state) => {
      state.error = null
    },
    fetchKioskConfigSuccess: (state, action: PayloadAction<KioskQueueConfig>) => {
      state.closed = action.payload.closed
      state.autoOpenTimeBulk = action.payload.autoOpenTimeBulk
      state.autoOpenTimeBags = action.payload.autoOpenTimeBags
      state.isLoaded = true
      state.error = null
    },
    fetchKioskConfigFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoaded = true
    },
  },
})

export const kioskConfigActions = kioskConfigSlice.actions
export const kioskConfigReducer = kioskConfigSlice.reducer

// ============================================================
// Selectors
// ============================================================

export interface KioskConfigRootState {
  kioskConfig: KioskConfigState
}

export function selectKioskConfig(state: KioskConfigRootState): KioskConfigState {
  return state.kioskConfig
}

export function selectIsPlantQueueClosed(
  state: KioskConfigRootState,
  plantDocumentId: string | null | undefined,
  packaging: KioskPacking | null | undefined,
): boolean {
  if (!plantDocumentId) return false
  // ponytail: missing packaging maps to 'bulk' (matches variant schema default)
  const bucket = packaging === 'bags' ? state.kioskConfig.closed.bags : state.kioskConfig.closed.bulk
  return bucket.includes(plantDocumentId)
}

// ============================================================
// Saga
// ============================================================

function asIdArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

export function parseKioskQueueConfig(data: unknown): KioskQueueConfig {
  const record = (data as Record<string, unknown>) ?? {}
  const raw = (record?.data ?? record) as Record<string, unknown> | null | undefined
  const config = raw?.kioskQueueConfig as Record<string, unknown> | null | undefined
  // ponytail: legacy `closedPlantIds` reproduces as closed for both packing types only when `closed` is absent entirely
  const closedRaw = config?.closed
  const hasClosedShape = closedRaw != null && typeof closedRaw === 'object'
  const legacy = asIdArray(config?.closedPlantIds)
  const bulkIds = hasClosedShape ? asIdArray((closedRaw as any)?.bulk) : legacy
  const bagsIds = hasClosedShape ? asIdArray((closedRaw as any)?.bags) : legacy
  return {
    closed: { bulk: bulkIds, bags: bagsIds },
    autoOpenTimeBulk: (config?.autoOpenTimeBulk as string | null) ?? null,
    autoOpenTimeBags: (config?.autoOpenTimeBags as string | null) ?? null,
  }
}

function* fetchKioskConfigSaga() {
  try {
    const http = getHttpClient()
    const res: { data: unknown } = yield call([http, http.get], '/api/portal-config')
    const config = parseKioskQueueConfig(res.data)
    yield put(kioskConfigActions.fetchKioskConfigSuccess(config))
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to load kiosk config'
    yield put(kioskConfigActions.fetchKioskConfigFailure(message))
  }
}

export function* kioskConfigSaga() {
  yield takeLatest(kioskConfigActions.fetchKioskConfig.type, fetchKioskConfigSaga)
}
